// Builds the seed messages that promote a proactive card thread (place
// suggestions / geofence removals) into a real Scout dialog.
//
// The cards are represented with the internal `tool_call` block type (the same
// model-agnostic type the AI worker emits and Dialog.vue renders) rather than
// the Anthropic-native `tool_use`, so the seeded card renders on chat reload.
// The assistant `tool_call` is paired with a `tool_result` acknowledgement so
// the persisted history is a valid, paired tool call — an unpaired tool call
// makes the next model turn fail ("tool_use ids were found without tool_result").

type SeedRole = 'assistant' | 'tool' | 'user';

type SeedContentBlock =
  | { type: 'text'; text: string }
  | {
      type: 'tool_call';
      id: string;
      name: 'ui_suggestion_card' | 'ui_removal_card';
      arguments: Record<string, string[]>;
    }
  | { type: 'tool_result'; tool_call_id: string; content: string };

export type SeedMessage = { role: SeedRole; content: SeedContentBlock[] };

type CardSeed =
  | { toolName: 'ui_suggestion_card'; idsKey: 'suggestion_ids'; label: string }
  | { toolName: 'ui_removal_card'; idsKey: 'removal_ids'; label: string };

// A tool_call id has to round-trip through the model API as an Anthropic
// tool_use id (^[a-zA-Z0-9_-]+$). We only need uniqueness within a single
// seeded dialog, so a timestamp + random suffix is sufficient and avoids
// relying on crypto.randomUUID being present in every runtime.
function seedToolCallId(): string {
  const rand = Math.random().toString(36).slice(2, 12);
  return `toolu_seed_${Date.now().toString(36)}${rand}`;
}

export function buildCardSeedMessages(
  seed: CardSeed,
  ids: string[],
  userText: string,
): SeedMessage[] {
  const toolCallId = seedToolCallId();
  const ack = JSON.stringify({
    content: [
      {
        type: 'text',
        text: `Displayed ${ids.length} ${seed.label}${ids.length === 1 ? '' : 's'} to the user.`,
      },
    ],
    isError: false,
  });

  return [
    {
      role: 'assistant',
      content: [
        {
          type: 'tool_call',
          id: toolCallId,
          name: seed.toolName,
          arguments: { [seed.idsKey]: ids },
        },
      ],
    },
    {
      role: 'tool',
      content: [
        { type: 'tool_result', tool_call_id: toolCallId, content: ack },
      ],
    },
    {
      role: 'user',
      content: [{ type: 'text', text: userText }],
    },
  ];
}
